import { useCallback, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Loader2, Play, Square, X } from "lucide-react";
import { postEvaluatePizzaAdvanced } from "@/api/pizza-checker/pizzas/pizzas";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import { errorMessage, fetchUnevaluatedFresh, useMonitoringUnevaluated } from "../api";
import { EmptyBlock, ErrorBlock, FetchingDot, LoadingBlock, SectionCard } from "../components/primitives";
import { formatClock, formatCount } from "../format";
import type { UnevaluatedDay, UnevaluatedPizza, UnevaluatedReason, UnevaluatedSection } from "../types";
import type { Tone } from "./pipeline/helpers";
import { Chip, PizzaRef, StatTile } from "./pipeline/shared";

const PERIODS = [7, 30, 90] as const;
const DEFAULT_PERIOD = 30;
const DAYS_PARAM = "days";
/** evaluations at a time: a live pizza must not wait behind the run (4 at once was harmless on prod) */
const PARALLEL = 3;

const REASON_TONE: Record<UnevaluatedReason, Tone> = {
  pending: "neutral",
  no_photo: "warn",
  photo_lost: "warn",
  no_pod: "nok",
  pod_failed: "nok",
  evaluation_error: "nok",
  no_record: "neutral",
  no_record_old: "neutral",
};

/** most actionable first */
const REASON_ORDER: UnevaluatedReason[] = [
  "no_pod", "pod_failed", "evaluation_error", "photo_lost", "no_photo", "no_record", "no_record_old", "pending",
];

type PizzaResult = { status: "running" | "ok" | "failed"; classification?: string | null; error?: string };

interface RunState {
  /** a day (YYYY-MM-DD) or one pizza id */
  key: string;
  total: number;
  done: number;
  ok: number;
  failed: number;
  finished: boolean;
  stopped: boolean;
}

/**
 * Evaluates pizzas one by one through the admin evaluate endpoint (reevaluate off: a pizza that got a
 * verdict meanwhile keeps it), PARALLEL at a time; one run at a time. Refreshes the section when done.
 */
function useEvaluationRun(days: number) {
  const queryClient = useQueryClient();
  const [run, setRun] = useState<RunState | null>(null);
  const [results, setResults] = useState<Record<string, PizzaResult>>({});
  const runningRef = useRef(false);
  const stopRef = useRef(false);

  const start = useCallback(
    async (key: string, ids: string[]) => {
      if (runningRef.current || ids.length === 0) return;
      runningRef.current = true;
      stopRef.current = false;
      setRun({ key, total: ids.length, done: 0, ok: 0, failed: 0, finished: false, stopped: false });
      const queue = [...ids];
      const worker = async () => {
        while (queue.length > 0 && !stopRef.current) {
          const id = queue.shift() as string;
          setResults((current) => ({ ...current, [id]: { status: "running" } }));
          try {
            const data = await postEvaluatePizzaAdvanced({ pizza_id: id, reevaluate: false });
            const classification = (data as { metadata?: { classification?: string } })?.metadata?.classification ?? null;
            setResults((current) => ({ ...current, [id]: { status: "ok", classification } }));
            setRun((state) => state && { ...state, done: state.done + 1, ok: state.ok + 1 });
          } catch (error) {
            setResults((current) => ({ ...current, [id]: { status: "failed", error: errorMessage(error) } }));
            setRun((state) => state && { ...state, done: state.done + 1, failed: state.failed + 1 });
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(PARALLEL, ids.length) }, worker));
      setRun((state) => state && { ...state, finished: true, stopped: stopRef.current });
      runningRef.current = false;
      try {
        queryClient.setQueryData(["monitoring", "unevaluated", days], await fetchUnevaluatedFresh(days));
      } catch {
        void queryClient.invalidateQueries({ queryKey: ["monitoring", "unevaluated", days] });
      }
    },
    [days, queryClient],
  );

  const stop = useCallback(() => {
    stopRef.current = true;
  }, []);
  const dismiss = useCallback(() => setRun((state) => (state && !state.finished ? state : null)), []);

  return { run, results, start, stop, dismiss, busy: !!run && !run.finished };
}

type Run = ReturnType<typeof useEvaluationRun>;

/** Captured pizzas that never got a verdict, per local day, with the reason and a way to evaluate them again. */
export function UnevaluatedTab() {
  const { t } = useLocalization();
  const [params, setParams] = useSearchParams();
  const param = Number(params.get(DAYS_PARAM));
  const days = (PERIODS as readonly number[]).includes(param) ? param : DEFAULT_PERIOD;
  const query = useMonitoringUnevaluated(days);
  const run = useEvaluationRun(days);
  const data = query.data;

  const setDays = (value: number) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value === DEFAULT_PERIOD) next.delete(DAYS_PARAM);
        else next.set(DAYS_PARAM, String(value));
        return next;
      },
      { replace: true },
    );

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {query.isError && <ErrorBlock message={errorMessage(query.error)} onRetry={() => void query.refetch()} />}
      <SectionCard
        title={
          <span className="inline-flex items-center gap-2">
            {t("monitoring.unevaluated.title")}
            <FetchingDot active={query.isFetching} />
          </span>
        }
        description={t("monitoring.unevaluated.description", {
          timezone: data?.timezone ?? "Europe/Prague",
          days: data?.event_retention_days ?? 30,
        })}
        actions={<PeriodSelect days={days} onChange={setDays} />}
      >
        {data ? <Totals data={data} /> : <LoadingBlock height={88} />}
        {run.run && <RunBanner run={run} />}
      </SectionCard>
      {data &&
        (data.by_day.length === 0 ? (
          <EmptyBlock>{t("monitoring.unevaluated.empty")}</EmptyBlock>
        ) : (
          <SectionCard title={t("monitoring.unevaluated.period.long", { count: days })} contentClassName="p-0">
            <ul className="divide-y divide-border/60">
              {data.by_day.map((day) => (
                <DayRow key={day.day} day={day} data={data} run={run} />
              ))}
            </ul>
            {data.truncated && (
              <p className="border-t px-4 py-2 text-[11px] text-muted-foreground">
                {t("monitoring.unevaluated.truncated", { count: data.total })}
              </p>
            )}
          </SectionCard>
        ))}
    </div>
  );
}

function PeriodSelect({ days, onChange }: { days: number; onChange: (days: number) => void }) {
  const { t } = useLocalization();
  return (
    <ToggleGroup
      type="single"
      size="sm"
      variant="outline"
      value={String(days)}
      onValueChange={(value) => value && onChange(Number(value))}
      aria-label={t("monitoring.unevaluated.period")}
      className="gap-0.5"
    >
      {PERIODS.map((value) => (
        <ToggleGroupItem
          key={value}
          value={String(value)}
          className="h-8 whitespace-nowrap px-2.5 text-xs"
          aria-label={t("monitoring.unevaluated.period.long", { count: value })}
        >
          {t("monitoring.unevaluated.period.days", { count: value })}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

function Totals({ data }: { data: UnevaluatedSection }) {
  const { t } = useLocalization();
  const noPhoto = data.reasons.no_photo + data.reasons.photo_lost;
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-3 gap-2">
        <StatTile
          label={t("monitoring.unevaluated.total")}
          value={formatCount(data.total)}
          hint={t("monitoring.unevaluated.totalHint")}
          tone={data.total > 0 ? "warn" : "neutral"}
        />
        <StatTile
          label={t("monitoring.unevaluated.evaluable")}
          value={formatCount(data.evaluable)}
          hint={t("monitoring.unevaluated.evaluableHint")}
        />
        <StatTile
          label={t("monitoring.unevaluated.noPhoto")}
          value={formatCount(noPhoto)}
          hint={t("monitoring.unevaluated.noPhotoHint")}
        />
      </div>
      <ReasonChips reasons={data.reasons} days={data.event_retention_days} />
    </div>
  );
}

function ReasonChips({ reasons, days }: { reasons: Record<UnevaluatedReason, number>; days: number }) {
  const present = REASON_ORDER.filter((reason) => (reasons[reason] ?? 0) > 0);
  if (!present.length) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {present.map((reason) => (
        <ReasonChip key={reason} reason={reason} count={reasons[reason]} days={days} />
      ))}
    </div>
  );
}

function ReasonChip({ reason, count, days }: { reason: UnevaluatedReason; count?: number; days: number }) {
  const { t } = useLocalization();
  return (
    <Chip tone={REASON_TONE[reason]} title={t(`monitoring.unevaluated.reason.${reason}Hint`, { days })}>
      {t(`monitoring.unevaluated.reason.${reason}`)}
      {count !== undefined && <span className="tabular-nums">{formatCount(count)}</span>}
    </Chip>
  );
}

function RunBanner({ run }: { run: Run }) {
  const { t, tp } = useLocalization();
  const state = run.run;
  if (!state) return null;
  const counts = { ok: formatCount(state.ok), failed: formatCount(state.failed) };
  return (
    <div
      role="status"
      className={cn(
        "mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border px-3 py-2 text-xs",
        state.finished && state.failed > 0 ? "border-amber-500/35" : "border-border",
      )}
    >
      {state.finished ? (
        <span>{t(state.stopped ? "monitoring.unevaluated.stopped" : "monitoring.unevaluated.finished", counts)}</span>
      ) : (
        <span className="inline-flex items-center gap-1.5">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          {t("monitoring.unevaluated.progress", { done: formatCount(state.done), total: formatCount(state.total) })}
          {state.failed > 0 && (
            <span className="text-amber-300"> · {tp("monitoring.unevaluated.progressFailed", state.failed)}</span>
          )}
        </span>
      )}
      <span className="ml-auto">
        {state.finished ? (
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={run.dismiss} aria-label="×">
            <X className="h-3.5 w-3.5" />
          </Button>
        ) : (
          <Button variant="outline" size="sm" className="h-7 gap-1.5 px-2 text-xs" onClick={run.stop}>
            <Square className="h-3 w-3" aria-hidden />
            {t("monitoring.unevaluated.stop")}
          </Button>
        )}
      </span>
    </div>
  );
}

const dayLabel = (day: string, locale: string) => {
  const [year, month, date] = day.split("-").map(Number);
  return new Date(year, month - 1, date).toLocaleDateString(locale, {
    weekday: "short",
    day: "numeric",
    month: "numeric",
    year: "numeric",
  });
};

function DayRow({ day, data, run }: { day: UnevaluatedDay; data: UnevaluatedSection; run: Run }) {
  const { t, tp, intlLocale } = useLocalization();
  const [open, setOpen] = useState(false);
  const evaluable = day.pizzas.filter((pizza) => pizza.evaluable).map((pizza) => pizza.id);
  const running = run.busy && run.run?.key === day.day;

  return (
    <li className="min-w-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          title={t(open ? "monitoring.unevaluated.hidePizzas" : "monitoring.unevaluated.showPizzas")}
          className="flex min-w-0 items-center gap-2 rounded text-left hover:text-foreground"
        >
          <ChevronRight className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-90")} />
          <span className="text-sm font-medium tabular-nums">{dayLabel(day.day, intlLocale)}</span>
          <span className="text-xs tabular-nums text-muted-foreground">{tp("monitoring.unevaluated.pizzas", day.total)}</span>
        </button>
        <div className="flex min-w-0 flex-wrap gap-1">
          {day.branches.map((branch) => (
            <Chip key={branch.id ?? "none"}>
              {branch.name || t("monitoring.unknownBranch")} <span className="tabular-nums">{formatCount(branch.count)}</span>
            </Chip>
          ))}
        </div>
        <ReasonChips reasons={day.reasons} days={data.event_retention_days} />
        <div className="ml-auto flex items-center gap-2">
          {running && run.run ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              {t("monitoring.unevaluated.progress", { done: formatCount(run.run.done), total: formatCount(run.run.total) })}
            </span>
          ) : (
            evaluable.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 px-2.5 text-xs"
                disabled={run.busy}
                title={run.busy ? t("monitoring.unevaluated.busy") : t("monitoring.unevaluated.evaluateDayHint", { parallel: PARALLEL })}
                onClick={() => {
                  setOpen(true);
                  void run.start(day.day, evaluable);
                }}
              >
                <Play className="h-3.5 w-3.5" aria-hidden />
                {t("monitoring.unevaluated.evaluateDay", { count: formatCount(evaluable.length) })}
              </Button>
            )
          )}
        </div>
      </div>
      {open && (
        <ul className="divide-y divide-border/40 border-t bg-muted/20">
          {day.pizzas.map((pizza) => (
            <PizzaRow key={pizza.id} pizza={pizza} run={run} days={data.event_retention_days} />
          ))}
        </ul>
      )}
    </li>
  );
}

function PizzaRow({ pizza, run, days }: { pizza: UnevaluatedPizza; run: Run; days: number }) {
  const { t } = useLocalization();
  const result = run.results[pizza.id];
  const photo =
    pizza.photo === null
      ? { label: t("monitoring.unevaluated.photo.unknown"), title: t("monitoring.unevaluated.photo.unknownHint") }
      : { label: t(pizza.photo ? "monitoring.unevaluated.photo.yes" : "monitoring.unevaluated.photo.no"), title: undefined };

  return (
    <li className="flex min-w-0 flex-col gap-1.5 px-4 py-2.5 sm:pl-10">
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
        <PizzaRef id={pizza.id} name={pizza.name} className="flex-1" />
        <div className="flex shrink-0 flex-col items-end gap-0.5 text-xs">
          <span>
            <span className="text-muted-foreground">{t("monitoring.unevaluated.capturedAt")} </span>
            <span className="tabular-nums">{formatClock(pizza.capture_at)}</span>
          </span>
          <span className="text-[11px] text-muted-foreground">{pizza.branch_name || t("monitoring.unknownBranch")}</span>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <ReasonChip reason={pizza.reason} days={days} />
        <Chip tone={pizza.photo === false ? "warn" : "neutral"} title={photo.title}>
          {photo.label}
        </Chip>
        {result && <ResultChip result={result} />}
        {pizza.evaluable && result?.status !== "ok" && (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto h-7 gap-1.5 px-2 text-xs"
            disabled={run.busy}
            title={run.busy ? t("monitoring.unevaluated.busy") : undefined}
            onClick={() => void run.start(pizza.id, [pizza.id])}
          >
            <Play className="h-3 w-3" aria-hidden />
            {t("monitoring.unevaluated.evaluateOne")}
          </Button>
        )}
      </div>
      <p className="text-[11px] leading-snug text-muted-foreground">
        {t(`monitoring.unevaluated.reason.${pizza.reason}Hint`, { days })}
      </p>
      {pizza.event && (
        <div className="break-words font-mono text-[11px] leading-snug text-muted-foreground">
          <span className="font-sans">{t("monitoring.unevaluated.lastError")}: </span>
          {pizza.event.message}
        </div>
      )}
      {result?.status === "failed" && result.error && (
        <div className="break-words font-mono text-[11px] leading-snug text-red-300">{result.error}</div>
      )}
    </li>
  );
}

function ResultChip({ result }: { result: PizzaResult }) {
  const { t } = useLocalization();
  if (result.status === "running") {
    return (
      <Chip>
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        {t("monitoring.unevaluated.result.running")}
      </Chip>
    );
  }
  if (result.status === "failed") return <Chip tone="nok">{t("monitoring.unevaluated.result.failed")}</Chip>;
  return (
    <Chip className="border-emerald-500/40 text-emerald-300">
      {t("monitoring.unevaluated.result.ok")}
      {result.classification ? ` · ${result.classification}` : ""}
    </Chip>
  );
}
